import { Box, Skeleton, Stack } from "@mui/material";
import { useErrorHandler } from "@smartrr/shared/components/ErrorBoundary";
import { InfiniteScroll } from "@smartrr/shared/components/InfiniteScroll";
import { CurrencyCode } from "@smartrr/shared/currencyCode";
import { AquariusButton } from "@smartrr/shared/customerPortal/components/AquariusButton";
import { AquariusButtonGroup } from "@smartrr/shared/customerPortal/components/AquariusButtonGroup";
import { TitleAndSearchBar } from "@smartrr/shared/customerPortal/components/TitleAndSearchBar";
import { IPurchasable } from "@smartrr/shared/entities/Purchasable";
import { SellingPlanConfigs } from "@smartrr/shared/entities/SellingPlanGroup";
import { shopifyGidToNumber } from "@smartrr/shared/utils/ensureShopifyGid";
import { localize, typedLocalize } from "@smartrr/shared/utils/sharedTranslations/localize";
import { useVariantToPurchasableMap } from "@smartrr/shared/utils/useVariantToPurchasableMap";
import isEqual from 'lodash/isEqual';
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { toast } from "react-toastify";

import { handleValidatingPurchasables, smartrrAccountConfig } from "@customer-app/account/config";
import { useToggleModal } from "@customer-app/state/actionCreators/modal";
import {
  VariantPurchaseOptions,
  useConfigToSellingPlanGroup,
} from "@customer-app/state/reducers/sellingPlanGroups";
import { useSmartrrCustomerSelector } from "@customer-app/state/typedCustomerReduxHooks";

import { MemoizedAddonItem } from "./libs/components/AddonItem";
import { FilterTagRow } from "./libs/components/FilterTagRow";
import { ItemAddingNoticeText } from "./libs/components/ItemAddingNoticeText";
import { NoProducts } from "./libs/components/NoProducts";
import { ActiveAddonProvider, useActiveAddon } from "./libs/hooks/useActiveAddon";
import { AddonListContentProps, AddonListProps } from "./libs/types";
import { getAllSmartrrFilterTags, removeSmartrrProductTag } from "./libs/utils";
import { useSmartrrTheme } from "../../../../hooks/useSmartrrTheme";
import { useMobileQuery } from "../../../../utils";
import { useAllBundles } from "../../../hooks/useCurrentBundle";
import { useGetAllSellingPlanGroups } from "../../../hooks/useGetAllSellingPlanGroups";
import { useSubscriptionActionStates, useSubscriptionActions } from "../../../store/SubscriptionActionStore";
import { AddOnSectionWrapper } from "../constants";
import { PurchaseStateAndDelivery } from "@smartrr/shared/entities/PurchaseState";
import { DeliveryAccess } from "@customer-app/state/stores/delivery";
import { getDeliveryId } from "@customer-app/state/stores/delivery/DeliveryId";

export const AddonList = ({ deliveryId, ...props }: AddonListProps) => {
  const delivery = DeliveryAccess.useDelivery(deliveryId);

  if (!delivery) {
    return <React.Fragment />;
  }

  return <AddonListContainer {...props} delivery={delivery} />;
};

interface AddonListContainerProps extends Omit<AddonListProps, "deliveryId"> {
  delivery: PurchaseStateAndDelivery;
}

export const AddonListContainer = ({
  purchasables,
  delivery,
  addItemAsSubscription,
  configType,
  ...props
}: AddonListContainerProps) => {
  const { hiddenProducts } = smartrrAccountConfig;

  const { purchaseState } = delivery;
  const variantToPurchasableMap = useVariantToPurchasableMap(purchasables);
  const sellingPlanConfig = useConfigToSellingPlanGroup(configType);

  const sellingPlanGroups = useSmartrrCustomerSelector(state => state.sellingPlanGroups.sellingPlanGroupList);
  const { hideAllBundleItems } = useAllBundles();
  const hasBundle = purchaseState.containsBundle;

  const [sellingPlanGroupProductIds, setSellingPlanGroupProductIds] = useState<number[]>();
  const [sellingPlanGroupVariantIds, setSellingPlanGroupVariantIds] = useState<number[]>();

  const cpsSellingPlanGroup = useMemo(
    () =>
      sellingPlanGroups.find(group =>
        group.sellingPlans.some(sellingPlan => {
          if (purchaseState.sellingPlanId && sellingPlan.shopifyId === purchaseState.sellingPlanId) {
            return true;
          }
          return false;
        })
      ),
    [purchaseState]
  );

  const activeSellingPlanGroup = useMemo(() => {
    if ((addItemAsSubscription && cpsSellingPlanGroup) || !sellingPlanConfig) {
      return cpsSellingPlanGroup;
    }
    return sellingPlanConfig!;
  }, [addItemAsSubscription, cpsSellingPlanGroup, sellingPlanConfig]);

  const { getMaxQuantity, isValidatedAddOn } = handleValidatingPurchasables(
    purchaseState,
    purchasables,
    variantToPurchasableMap
  );

  const allAddonsPurchasable = useMemo(() => {
    return purchasables.filter(p => {
      let condition = true;
      if (p.shopifyId) {
        const isValidated = isValidatedAddOn(p.shopifyId);
        const withinProductList = !!sellingPlanGroupProductIds?.includes(shopifyGidToNumber(p.shopifyId));
        const withinVariantList =
          (!!p.vnts?.length &&
            p.vnts.some(vnt => sellingPlanGroupVariantIds?.includes(shopifyGidToNumber(vnt.shopifyId!)))) ??
          false;

        condition = condition && (withinProductList || withinVariantList) && isValidated;
      }

      if (hiddenProducts?.length) {
        const shopifyId = p.shopifyId ? shopifyGidToNumber(p.shopifyId).toString() : "";

        condition = condition && !hiddenProducts?.includes(shopifyId);
      }

      return condition;
    });
  }, [
    purchasables,
    hiddenProducts,
    sellingPlanGroupProductIds,
    hasBundle,
    sellingPlanGroupVariantIds,
    hideAllBundleItems,
  ]);

  useEffect(() => {
    if (!sellingPlanGroupProductIds && activeSellingPlanGroup?.productIds.length) {
      setSellingPlanGroupProductIds(activeSellingPlanGroup.productIds);
      setSellingPlanGroupVariantIds(activeSellingPlanGroup.variantIds);
    }
  }, [activeSellingPlanGroup]);

  if (
    !activeSellingPlanGroup ||
    !allAddonsPurchasable.length ||
    (!sellingPlanGroupProductIds?.length && !sellingPlanGroupVariantIds?.length)
  ) {
    return <React.Fragment />;
  }

  return (
    <ActiveAddonProvider>
      <MemoizedAddonList
        configType={configType}
        purchasables={allAddonsPurchasable}
        getMaxQuantity={getMaxQuantity}
        variantToPurchasableMap={variantToPurchasableMap}
        activeSellingPlanGroup={activeSellingPlanGroup}
        addItemAsSubscription={addItemAsSubscription}
        deliveryId={getDeliveryId(delivery)}
        {...props}
      />
    </ActiveAddonProvider>
  );
};

function AddonListContent({ deliveryId, ...props }: AddonListContentProps): JSX.Element {
  const delivery = DeliveryAccess.useDelivery(deliveryId);

  if (!delivery) {
    return <React.Fragment />;
  }

  return <AddonListContentContainer {...props} delivery={delivery} />;
}

interface AddonListContentContainerProps extends Omit<AddonListContentProps, "deliveryId"> {
  delivery: PurchaseStateAndDelivery;
}

function AddonListContentContainer({
  delivery,
  purchasables,
  title,
  onCancel,
  getMaxQuantity,
  variantToPurchasableMap,
  addItemAsSubscription = false,
  showItemAddingNoticeText = false,
  configType = SellingPlanConfigs.ADDON,
  portalNodeEleOverride,
}: AddonListContentContainerProps): JSX.Element {
  const isMobile = useMobileQuery();
  const handleError = useErrorHandler();
  const theme = useSmartrrTheme();
  const toggleModal = useToggleModal();
  const { activeAddonId, setActiveAddonId } = useActiveAddon();
  const boxRef = useRef<HTMLDivElement | null>(null);
  const { onAddItem } = useSubscriptionActions();
  const { isLoading } = useSubscriptionActionStates();
  const customerCountryCode = useSmartrrCustomerSelector(
    state => state.auth.customerRelationship?.defaultAddress?.countryCode
  );
  const [subscribedPurchasablesSearchText, setSubscribedPurchasablesSearchText] = useState<string>("");

  const [addonsToShow, setAddonsToShow] = useState<IPurchasable[]>([]);
  const [activeTag, setActiveTag] = useState<string | undefined>();

  const inModalView = !!onCancel;
  const { purchaseState } = delivery;
  const { sellingPlanId } = purchaseState;
  const currency = purchaseState.currency as CurrencyCode;

  const searchFilteredAddons = useMemo(
    () =>
      purchasables.filter(p =>
        p.purchasableName.toLocaleLowerCase().includes(subscribedPurchasablesSearchText.toLocaleLowerCase())
      ),
    [purchasables.length, subscribedPurchasablesSearchText]
  );

  const tagFilteredAddons = useMemo(
    () =>
      searchFilteredAddons.filter(p => {
        if (!activeTag) {
          return true;
        }
        if (!p.smartrrTags) {
          return false;
        }

        return p.smartrrTags
          .map(removeSmartrrProductTag)
          .some(tag => tag.toLocaleLowerCase() == activeTag.toLocaleLowerCase());
      }),
    [searchFilteredAddons.length, activeTag]
  );

  const allFilterTags = useMemo(
    () => getAllSmartrrFilterTags(searchFilteredAddons),
    [searchFilteredAddons.length]
  );

  const showFilters = configType === SellingPlanConfigs.ADDON || configType === SellingPlanConfigs.SUBSCRIPTION;

  const groups = useGetAllSellingPlanGroups();

  const onAddItemSubmit = useCallback(
    async (selectedOption: VariantPurchaseOptions, quantity: number) =>
      await onAddItem(
        delivery,
        selectedOption,
        quantity,
        groups,
        {
          addItemAsSubscription,
          configType,
          scheduleIndex: delivery.indexFromScheduleStart,
        },
        customerCountryCode
      )
        .then(() => {
          toast(
            typedLocalize(`subscriptions.yourSubscriptions.orderOptionsModal.addItemView.addedProductToast`, {
              productName: variantToPurchasableMap[selectedOption.vnt.id]?.purchasableName ?? "",
            })
          );
        })
        .catch(error => {
          handleError(error);
        })
        .finally(() => {
          if (onCancel) {
            onCancel();
          }
        }),
    [onAddItem, groups, addItemAsSubscription, configType, setActiveAddonId, handleError, onCancel]
  );

  const returnToAddonList = useCallback(() => {
    toggleModal("ADDON_LIST", {
      deliveryId: getDeliveryId(delivery),
      purchasables,
      title,
      configType,
      onCancel,
      addItemAsSubscription,
      showItemAddingNoticeText,
    });
  }, [delivery, purchasables, title, configType, addItemAsSubscription, showItemAddingNoticeText]);

  const onCancelHandler = useCallback(() => {
    onCancel && onCancel();
    setActiveAddonId(null);
  }, []);

  useEffect(() => {
    // Setting initial batch of 10 addons once purchaseables loads or search text changes
    setAddonsToShow(tagFilteredAddons.slice(0, 10));
  }, [tagFilteredAddons.length, subscribedPurchasablesSearchText, activeTag]);

  // if component is used within a modal, onCancel is passed in.
  // we can then decide to hide the component if on main screen or display null state if within modal
  if (!tagFilteredAddons.length && !subscribedPurchasablesSearchText && !onCancel) {
    return <React.Fragment />;
  }

  const NullProductsComponent = () => {
    if (subscribedPurchasablesSearchText) {
      return <NoProducts height={inModalView || isMobile ? "260px" : "100%"} />;
    }
    return <React.Fragment />;
  };
  return (
    <AddOnSectionWrapper data-testid="addon-section-wrapper" pt={inModalView ? "20px" : 0}>
      {title ? (
        <TitleAndSearchBar
          title={title}
          isMobile={isMobile}
          subscribedPurchasablesSearchText={subscribedPurchasablesSearchText}
          updateSubscribedPurchasablesSearchText={setSubscribedPurchasablesSearchText}
        />
      ) : null}
      {showFilters ? (
        <FilterTagRow onTagSelect={tag => setActiveTag(tag)} tags={allFilterTags} activeTag={activeTag} />
      ) : (
        <React.Fragment />
      )}
      {(tagFilteredAddons.length > 0 && (
        <React.Fragment>
          <Box
            sx={{
              overflowX: "scroll",
              height: "max-content",
              overflowY: "hidden",
              marginTop: "15px",
              marginRight: "-20px",
              minHeight: inModalView ? "0px" : "214px",
              transition: "0.3s ease-in-out",
            }}
          >
            <InfiniteScroll
              isParentLoading={isLoading}
              hasMore={tagFilteredAddons.length > addonsToShow.length}
              skeleton={
                <Stack direction="column" gap="7px" p="10px">
                  <Skeleton
                    variant="rectangular"
                    width={90}
                    height={90}
                    sx={{
                      borderRadius: theme.modernImageBorderRadius + "px",
                    }}
                  />
                  <Skeleton
                    variant="rectangular"
                    width={90}
                    height={30}
                    sx={{
                      borderRadius: theme.modernButtonBorderRadius + "px",
                    }}
                  />
                  <Skeleton
                    variant="rectangular"
                    width={90}
                    height={60}
                    sx={{
                      borderRadius: theme.modernButtonBorderRadius + "px",
                    }}
                  />
                </Stack>
              }
              horizontal={true}
              height="100%"
              width="100%"
              gap="10px"
              handleAddItemsToScroll={numberOfItemsToLoad =>
                setAddonsToShow(prevAddons => [
                  ...prevAddons,
                  ...tagFilteredAddons.slice(addonsToShow.length, numberOfItemsToLoad),
                ])
              }
            >
              {addonsToShow.map((prod: IPurchasable) => {
                const maxQuantity = getMaxQuantity(prod.shopifyId || "");
                return (
                  <MemoizedAddonItem
                    maxQuantity={maxQuantity}
                    key={prod.id}
                    addonProduct={prod}
                    currency={currency}
                    isLoading={isLoading}
                    sellingPlanId={sellingPlanId}
                    portalNodeEle={portalNodeEleOverride?.current ?? boxRef.current!}
                    onAddItemSubmit={onAddItemSubmit}
                    delivery={delivery}
                    renderItemsForSubscription={addItemAsSubscription}
                    selectOnClick={inModalView}
                    configType={configType}
                    {...(inModalView ? { returnToAddonList } : null)}
                  />
                );
              })}
            </InfiniteScroll>
          </Box>
          <Box ref={boxRef}></Box>
          {inModalView && !activeAddonId ? (
            <React.Fragment>
              <AquariusButtonGroup topBorder>
                <AquariusButton
                  onClick={() => onCancelHandler()}
                  disabled={isLoading}
                  data-testid="addon-list-modal-cancel-button"
                >
                  {localize(`general.cancelBtn`)}
                </AquariusButton>
                <React.Fragment />
              </AquariusButtonGroup>
            </React.Fragment>
          ) : null}
          {showItemAddingNoticeText ? <ItemAddingNoticeText /> : null}
        </React.Fragment>
      )) || <NullProductsComponent />}
    </AddOnSectionWrapper>
  );
}

export const MemoizedAddonList = React.memo(AddonListContent, (prev, current) =>
  isEqual(prev.purchasables, current.purchasables)
);
